
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { HelpCircle, Plus, X, ArrowRight, SkipForward, FileText, Lightbulb, ChevronDown, ChevronUp, Sparkles } from 'lucide-react';
import { Question, QuestionTemplate, QUESTION_TEMPLATES } from '@/types/questions';

interface QuestionSetupProps {
  onComplete: (big3Questions: string[], backupQuestions: string[]) => void;
  spaceId?: string;
  saveQuestions?: (big3Questions: string[], backupQuestions: string[]) => Promise<void>;
}

export const QuestionSetup = ({ onComplete, spaceId, saveQuestions }: QuestionSetupProps) => {
  const [big3Questions, setBig3Questions] = useState<string[]>(['', '', '']);
  const [backupQuestions, setBackupQuestions] = useState<string[]>(['']);
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'manual' | 'template'>('manual');
  const [isBackupExpanded, setIsBackupExpanded] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState<string[]>([]);
  const [isGeneratingSuggestions, setIsGeneratingSuggestions] = useState(false);

  // Generate suggestions when Big 3 questions change
  useEffect(() => {
    // Generate AI suggestions based on Big 3 questions
    const generateAISuggestions = async () => {
      const validBig3 = big3Questions.filter(q => q.trim() !== '');
      if (validBig3.length === 0) return;

      setIsGeneratingSuggestions(true);
      try {
        // Simple AI suggestions based on question patterns
        const suggestions = validBig3.flatMap(question => {
          const lower = question.toLowerCase();
          if (lower.includes('goal') || lower.includes('want') || lower.includes('need')) {
            return [
              `What would success look like for ${lower.includes('goal') ? 'this goal' : 'you'}?`,
              `What's the biggest obstacle preventing you from achieving this?`,
              `How do you currently measure progress on this?`
            ];
          } else if (lower.includes('problem') || lower.includes('challenge') || lower.includes('pain')) {
            return [
              `How long has this been a problem?`,
              `What have you tried to solve this before?`,
              `Who else is affected by this issue?`
            ];
          } else if (lower.includes('use') || lower.includes('experience') || lower.includes('try')) {
            return [
              `What was your first impression?`,
              `How does this compare to what you were using before?`,
              `What would make this experience better?`
            ];
          } else {
            return [
              `Can you give me a specific example of that?`,
              `How often does this come up for you?`,
              `What would an ideal solution look like?`
            ];
          }
        });

        // Remove duplicates and limit to 5 suggestions
        const uniqueSuggestions = [...new Set(suggestions)].slice(0, 5);
        setAiSuggestions(uniqueSuggestions);
      } catch (error) {
        console.error('Error generating suggestions:', error);
      } finally {
        setIsGeneratingSuggestions(false);
      }
    };

    const debounceTimer = setTimeout(() => {
      generateAISuggestions();
    }, 1000);

    return () => clearTimeout(debounceTimer);
  }, [big3Questions]);

  const updateBig3Question = (index: number, value: string) => {
    const updated = [...big3Questions];
    updated[index] = value;
    setBig3Questions(updated);
  };

  const addBackupQuestion = () => {
    if (backupQuestions.length < 8) {
      setBackupQuestions([...backupQuestions, '']);
    }
  };

  const removeBackupQuestion = (index: number) => {
    if (backupQuestions.length > 1) {
      setBackupQuestions(backupQuestions.filter((_, i) => i !== index));
    }
  };

  const updateBackupQuestion = (index: number, value: string) => {
    const updated = [...backupQuestions];
    updated[index] = value;
    setBackupQuestions(updated);
  };

  const applyTemplate = (template: QuestionTemplate) => {
    setBig3Questions([...template.big3Questions]);
    setBackupQuestions([...template.backupQuestions]);
    setSelectedTemplate(template.id);
    setActiveTab('manual');
  };

  const addSuggestionToBackup = (suggestion: string) => {
    if (!backupQuestions.includes(suggestion) && backupQuestions.length < 8) {
      const emptyIndex = backupQuestions.findIndex(q => q.trim() === '');
      if (emptyIndex !== -1) {
        updateBackupQuestion(emptyIndex, suggestion);
      } else {
        setBackupQuestions([...backupQuestions, suggestion]);
      }
    }
  };

  const handleComplete = async () => {
    const validBig3 = big3Questions.filter(q => q.trim() !== '');
    const validBackup = backupQuestions.filter(q => q.trim() !== '');
    
    console.log('QuestionSetup: Completing with questions:', { big3: validBig3, backup: validBackup });
    
    // Save to database if we have the saveQuestions function
    if (saveQuestions) {
      try {
        await saveQuestions(validBig3, validBackup);
        console.log('Questions saved to database');
      } catch (error) {
        console.error('Error saving questions to database:', error);
      }
    }
    
    // Also save to localStorage as fallback (for existing functionality)
    if (spaceId) {
      const big3Objects = validBig3.map((text, index) => ({
        id: `big3-${Date.now()}-${index}`,
        text: text,
        type: 'open' as const,
        category: 'big3' as const
      }));
      
      const backupObjects = validBackup.map((text, index) => ({
        id: `backup-${Date.now()}-${index}`,
        text: text,
        type: 'open' as const,
        category: 'backup' as const
      }));
      
      localStorage.setItem(`yakstack_big3_${spaceId}`, JSON.stringify(big3Objects));
      localStorage.setItem(`yakstack_backup_${spaceId}`, JSON.stringify(backupObjects));
    }
    
    onComplete(validBig3, validBackup);
  };

  const handleSkip = () => {
    console.log('QuestionSetup: Skipping question setup');
    onComplete([], []);
  };

  const hasValidBig3 = big3Questions.some(q => q.trim() !== '');
  const hasValidBackup = backupQuestions.some(q => q.trim() !== '');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl mx-auto p-6 md:p-8 bg-white max-h-[95vh] overflow-y-auto">
        <div className="text-center mb-6">
          <div className="flex items-center justify-center mb-4">
              <img className='w-32 h-32' src='/yak-stak-logo.png' alt='Yakky' />
          </div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Setup Your Research Questions</h1>
          <p className="text-base md:text-lg text-gray-600 mb-4">
            Create your Big 3 guiding questions and backup questions for deeper exploration
          </p>
        </div>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'manual' | 'template')} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="manual" className="flex items-center space-x-2">
              <HelpCircle className="w-4 h-4" />
              <span>Manual Setup</span>
            </TabsTrigger>
            <TabsTrigger value="template" className="flex items-center space-x-2">
              <FileText className="w-4 h-4" />
              <span>Use Template</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="template" className="space-y-4">
            <div className="text-center mb-4">
              <p className="text-gray-600">Choose a template to get started quickly</p>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {QUESTION_TEMPLATES.map((template) => (
                <Card key={template.id} className="p-4 cursor-pointer hover:bg-gray-50 border-2 hover:border-amber-200 transition-colors">
                  <div className="space-y-3" onClick={() => applyTemplate(template)}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900">{template.name}</h3>
                      <Badge variant="outline">{template.category}</Badge>
                    </div>
                    <p className="text-sm text-gray-600">{template.description}</p>
                    <div className="space-y-2">
                      <div className="text-xs font-medium text-gray-500">Big 3 Questions:</div>
                      <div className="space-y-1">
                        {template.big3Questions.slice(0, 2).map((q, i) => (
                          <div key={i} className="text-xs text-gray-600 bg-amber-50 p-2 rounded">{q}</div>
                        ))}
                        {template.big3Questions.length > 2 && (
                          <div className="text-xs text-gray-500">+{template.big3Questions.length - 2} more...</div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="manual" className="space-y-6">
            {/* Big 3 Questions */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-semibold text-gray-900">Your Big 3 Questions</h2>
                <Badge variant="secondary">Primary Focus</Badge>
              </div>
              <p className="text-sm text-gray-600">These are your main research questions that will guide the entire interview</p>
              
              <div className="space-y-3">
                {big3Questions.map((question, index) => (
                  <div key={index} className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-3">
                    <Label className="text-sm font-medium text-amber-600 md:w-16 flex-shrink-0">
                      Q{index + 1}:
                    </Label>
                    <Input
                      value={question}
                      onChange={(e) => updateBig3Question(index, e.target.value)}
                      placeholder={`What's your ${index === 0 ? 'primary' : index === 1 ? 'secondary' : 'tertiary'} research question?`}
                      className="flex-1 border-amber-200 focus:border-amber-400"
                      autoFocus={index === 0}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* AI Suggestions */}
            {aiSuggestions.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <div className="flex items-center space-x-2">
                  <Sparkles className="w-4 h-4 text-blue-600" />
                  <h3 className="text-sm font-medium text-blue-900">AI-Suggested Backup Questions</h3>
                  <Badge variant="outline" className="text-xs">Based on your Big 3</Badge>
                </div>
                <div className="grid gap-2">
                  {aiSuggestions.map((suggestion, index) => (
                    <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                      <span className="text-sm text-gray-700">{suggestion}</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addSuggestionToBackup(suggestion)}
                        className="text-xs"
                      >
                        Add
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Backup Questions */}
            <Collapsible open={isBackupExpanded} onOpenChange={setIsBackupExpanded}>
              <CollapsibleTrigger asChild>
                <Button variant="outline" className="w-full flex items-center justify-between p-4 h-auto">
                  <div className="flex items-center space-x-2">
                    <Lightbulb className="w-4 h-4" />
                    <span>Backup Questions</span>
                    <Badge variant="outline">{backupQuestions.filter(q => q.trim()).length}/8</Badge>
                  </div>
                  {isBackupExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </CollapsibleTrigger>
              
              <CollapsibleContent className="space-y-4 mt-4">
                <p className="text-sm text-gray-600">
                  These questions will be available when you need to dig deeper or when conversations stall
                </p>
                
                <div className="space-y-3">
                  {backupQuestions.map((question, index) => (
                    <div key={index} className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-3">
                      <Label className="text-sm font-medium text-gray-600 md:w-16 flex-shrink-0">
                        B{index + 1}:
                      </Label>
                      <Input
                        value={question}
                        onChange={(e) => updateBackupQuestion(index, e.target.value)}
                        placeholder="What's a good follow-up or deeper question?"
                        className="flex-1"
                      />
                      {backupQuestions.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeBackupQuestion(index)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50 self-end md:self-auto"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>

                <Button
                  variant="outline"
                  onClick={addBackupQuestion}
                  disabled={backupQuestions.length >= 8}
                  className="flex items-center space-x-2"
                >
                  <Plus className="w-4 h-4" />
                  <span>Add Backup Question</span>
                </Button>
              </CollapsibleContent>
            </Collapsible>
          </TabsContent>
        </Tabs>

        {/* Action Buttons */}
        <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between space-y-3 md:space-y-0 pt-6 border-t">
          <div className="text-sm text-gray-500">
            {hasValidBig3 ? `${big3Questions.filter(q => q.trim()).length} Big 3` : 'No Big 3 questions'} 
            {hasValidBackup ? ` • ${backupQuestions.filter(q => q.trim()).length} backup questions` : ''}
          </div>

          <div className="flex space-x-3">
            <Button
              onClick={handleSkip}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <SkipForward className="w-4 h-4" />
              <span>Skip for now</span>
            </Button>

            <Button
              onClick={handleComplete}
              className="flex items-center space-x-2 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600"
            >
              <span>Start Interview</span>
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
